import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CompleteAccountBanner } from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import EnsureLoggedInContainer from '@/components/EnsureLoggedIn/EnsureLoggedInContainer';
import PageTitle from '@/components/PageTitle/PageTitle';
import EditPhoneNumber from '@/components/PhoneNumber/EditPhoneNumber';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import UnsubscribeEmail from '@/pages/AccountSettings/UnsubscribeEmail';
import { QueryParams } from '@/pages/Notifications/notifications.types';
import { AlertTypes, DeliveryType } from '@/redux/api/alerts';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    fetchEmailListsIfNeeded,
    toggleEmailListSubscription,
} from '@/redux/modules/account/notifications/emailList/emailList.actions';
import { UNSUBSCRIBE_TO_ALL_EMAIL_LISTS } from '@/redux/modules/account/notifications/emailList/emailList.types';
import { getBidderPhoneNumber, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getBrowserPermission } from '@/redux/modules/notification';
import { unsubscribeAllSavedSearchEmail } from '@/redux/modules/savedSearch';
import { fetchSmsAlertsIfNeeded, getSubscribedToSMSAlerts, toggleSmsAlert } from '@/redux/modules/smsAlerts';
import {
    TrackSmsNotificationToggleEventTrigger,
    useTrackSmsNotificationToggle,
} from '@/utils/amplitude/analytics/notifications';
import { parseQueryParams } from '@/utils/queryParams';
import { getNotificationsUrl, getProfileUrl } from '@/utils/urls';

import Alerts from './Alerts';
import { AlertTypeRow } from './Components/AlertTypeRow';
import NotificationsPageHeaderMeta from './Components/NotificationsPageHeaderMeta';
import FollowedSellersPage from './FollowedSellersPage';
import MySavedSearchPageManageContainer from './MySavedSearchPageManageContainer';
import Newsletters from './Newsletters';

const NotificationsPage = () => {
    const location = useLocation();
    const { section } = parseQueryParams<QueryParams>(location.search);
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const [reset, setReset] = useState(0);
    const [activeId, setActiveIdDefault] = useState<QueryParams['section'] | ''>(section || '');

    const loggedIn = useAppSelector(isUserLoggedIn);
    const subscribedToSMS = useAppSelector(getSubscribedToSMSAlerts);
    const phoneNumber = useAppSelector(getBidderPhoneNumber);

    const trackSmsNotificationToggle = useTrackSmsNotificationToggle();

    const setActiveId = useCallback(
        (activeId: QueryParams['section']) => {
            navigate(getNotificationsUrl(activeId), { replace: true });
            setActiveIdDefault(activeId);
        },
        [navigate]
    );

    const onUnsubscribeAllClicked = useCallback(() => {
        dispatch(toggleSmsAlert(AlertTypes.All, DeliveryType.unsubscribe, false));
        dispatch(toggleEmailListSubscription({ code: UNSUBSCRIBE_TO_ALL_EMAIL_LISTS }));
        dispatch(unsubscribeAllSavedSearchEmail());
        setReset(reset + 1);

        trackSmsNotificationToggle({
            auctionRemindersEnabled: false,
            eventTrigger: TrackSmsNotificationToggleEventTrigger.EmailsAndNotifications,
            outbidEnabled: false,
            paymentRemindersEnabled: false,
        });
    }, [dispatch, reset, trackSmsNotificationToggle]);

    useEffect(() => {
        dispatch(fetchEmailListsIfNeeded());
        dispatch(fetchSmsAlertsIfNeeded());
        dispatch(getBrowserPermission());
    }, [dispatch, loggedIn]);

    return (
        <EnsureLoggedInContainer>
            <div className="mx-auto w-[calc(100%-40px)] max-w-[1200px] self-center">
                <NotificationsPageHeaderMeta />
                <UnsubscribeEmail />
                <CompleteAccountBanner />
                <div className="flex items-end justify-between tabletMax:flex-col">
                    <PageTitle
                        className="ml-0"
                        title={<FormattedMessage id="notificationsPage.title" />}
                    />
                    {subscribedToSMS &&
                        (phoneNumber ? (
                            <BodyPrimary data-testid="phoneNumber">
                                <EditPhoneNumber />
                            </BodyPrimary>
                        ) : (
                            <RouteLink to={getProfileUrl()}>
                                <FormattedMessage id="notificationsPage.missingNumber" />
                            </RouteLink>
                        ))}
                </div>
                <AlertTypeRow />
                <Newsletters
                    activeId={activeId}
                    setActiveId={setActiveId}
                />
                <Alerts
                    activeId={activeId}
                    setActiveId={setActiveId}
                />
                <MySavedSearchPageManageContainer
                    activeId={activeId}
                    setActiveId={setActiveId}
                />
                <FollowedSellersPage
                    activeId={activeId}
                    setActiveId={setActiveId}
                />
                <Button
                    className="mt-lg"
                    data-testid="unsubscribeButton"
                    onClick={onUnsubscribeAllClicked}
                    size="sm"
                    variant="outlined"
                >
                    {formatMessage({ id: 'notificationsPage.unsubscribeAll' })}
                </Button>
            </div>
        </EnsureLoggedInContainer>
    );
};

export default NotificationsPage;
